import { useMyUser, useWriteQueryParam } from '@consigli/hooks';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { agentsCatalogue } from '@/util/agents-catalogue';
import { AGENT_CATEGORY, type AgentCategory, type AgentTag } from '@/util/service-config';

import {
  ALL_FILTER,
  type AgentCategoryFilter,
  type AgentTagOption,
  type DirectoryAgent,
  filterAgents,
} from './filter-agents';

/** Query params on `/agents`, so a filtered view is a link someone can send. */
export const SEARCH_PARAM = 'search';
export const CATEGORY_PARAM = 'category';
/** Comma-separated tag keys. */
export const TAGS_PARAM = 'tags';

const CATEGORIES: ReadonlySet<string> = new Set(Object.values(AGENT_CATEGORY));

const isCategory = (value: string | null): value is AgentCategory =>
  value !== null && CATEGORIES.has(value);

interface UseAgentsDirectory {
  /** Every agent visible to this user, unfiltered. The details dialog resolves `?agent=` against it. */
  catalogue: DirectoryAgent[];
  /** Visible to this user and matching all three facets, sorted by title. */
  agents: DirectoryAgent[];
  /** Every tag any visible agent carries, distinct and sorted by label. */
  tags: AgentTagOption[];
  category: AgentCategoryFilter;
  onCategoryChange: (category: AgentCategoryFilter) => void;
  /** Several at once; empty is unfiltered. Held in `tags` order, not click order. */
  selectedTags: AgentTag[];
  onSelectedTagsChange: (tags: AgentTag[]) => void;
  searchText: string;
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * The whole of the agents directory's data: the catalogue, the filter state, and
 * the filtered result.
 *
 * `useProjectServices` is not reusable here. It reads `useProjectId()` and gates
 * on the project's `effectiveServices` and the caller's role on it, which is the
 * right question for "what can I add to this project" and the wrong one for a
 * global catalogue — the directory shows what the product offers.
 *
 * Which agents that is, is product's list rather than a rule, and it lives in
 * `agentsCatalogue` so the loading strip reads the same list: the entries that
 * carry `categories` in `serviceConfig`, with Data Center and Wastewater
 * treatment gated by the user's licences. It settles what earlier versions of
 * this hook reasoned about and left open — BID_REVIEW and TENANT_REQUIREMENTS
 * are both off the list, so neither needs a special case here any more.
 *
 * Order is alphabetical by the translated title, so it is the order the reader
 * sees rather than the order the catalogue happens to be written in, and it
 * re-sorts with the language. The developer/contractor orderings belong to the
 * project's service list, where they rank by relevance to that project.
 */
export const useAgentsDirectory = (): UseAgentsDirectory => {
  const { user } = useMyUser();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const writeQueryParam = useWriteQueryParam();

  // Narrowed, because the URL is a boundary anyone can type into: an unknown
  // category reads as "All".
  const categoryParam = searchParams.get(CATEGORY_PARAM);
  const category: AgentCategoryFilter = isCategory(categoryParam) ? categoryParam : ALL_FILTER;
  const tagsParam = searchParams.get(TAGS_PARAM) ?? '';
  const urlSearch = searchParams.get(SEARCH_PARAM) ?? '';

  // Held here and not read off the URL: a data router applies a navigation a
  // tick later, so a controlled input fed from the URL would drop the caret to
  // the end mid-word.
  const [searchText, setSearchText] = useState(urlSearch);
  // What we last wrote, so a back press can be told from the echo of a keystroke.
  const lastWrittenSearch = useRef(urlSearch);

  useEffect(() => {
    if (urlSearch !== lastWrittenSearch.current) {
      lastWrittenSearch.current = urlSearch;
      setSearchText(urlSearch);
    }
  }, [urlSearch]);

  const agents = useMemo<DirectoryAgent[]>(
    () =>
      // The three fields, not `user`: the memo should hold across the many user
      // fields that change without affecting the catalogue.
      agentsCatalogue({
        isSuperuser: user.isSuperuser,
        solutionViewerDatacenterAccess: user.solutionViewerDatacenterAccess,
        solutionViewerWaterTreatmentAccess: user.solutionViewerWaterTreatmentAccess,
      })
        .map((service) => ({
          serviceName: service.serviceName,
          title: t(service.titleKey),
          // Wastewater treatment is categorised but hidden, so it has no
          // directory copy and would fall back to its service-card tooltip.
          description: t(service.directoryDescriptionKey ?? service.tooltipKey),
          categories: service.categories,
          tags: service.tags.map((key) => ({ key, label: t(`agents-directory.tags.${key}`) })),
        }))
        // By title, not by key: the titles are what the grid shows, and the
        // keys are English slugs — QA_AGENT would sort under Q in every
        // language while its card reads "Model Review".
        .sort((a, b) => a.title.localeCompare(b.title)),
    [
      t,
      user.isSuperuser,
      user.solutionViewerDatacenterAccess,
      user.solutionViewerWaterTreatmentAccess,
    ],
  );

  const tags = useMemo<AgentTagOption[]>(() => {
    // Keyed, so a tag several agents carry is listed once.
    const byKey = new Map<AgentTag, AgentTagOption>();
    for (const agent of agents) {
      for (const option of agent.tags) byKey.set(option.key, option);
    }

    // By label, not by key: the dropdown is read, and the keys are English
    // slugs — `mep` would sort under M in every locale otherwise.
    return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [agents]);

  // Unknown keys drop out, and the rest take the dropdown's order.
  const selectedTags = useMemo<AgentTag[]>(() => {
    const wanted = new Set(tagsParam.split(','));
    return tags.filter((option) => wanted.has(option.key)).map((option) => option.key);
  }, [tags, tagsParam]);

  const filtered = useMemo(
    () => filterAgents(agents, { category, tags: selectedTags, searchText }),
    [agents, category, selectedTags, searchText],
  );

  // Signature-compatible with `Input`'s onChange, as on the project list. No
  // debounce: filtering is local over 28 entries, so there is nothing to wait
  // for and a delay would only make the grid feel slower than it is.
  //
  // Replace, not push, so each keystroke is not a history entry. Back then skips
  // over a search to the filter change before it.
  const onSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setSearchText(next);
      lastWrittenSearch.current = next;
      writeQueryParam(SEARCH_PARAM, next, { replace: true });
    },
    [writeQueryParam],
  );

  // Pushed, so back undoes them. A default writes '', which removes the param.
  const onCategoryChange = useCallback(
    (next: AgentCategoryFilter) => writeQueryParam(CATEGORY_PARAM, next === ALL_FILTER ? '' : next),
    [writeQueryParam],
  );
  const onSelectedTagsChange = useCallback(
    (next: AgentTag[]) => writeQueryParam(TAGS_PARAM, next.join(',')),
    [writeQueryParam],
  );

  return {
    catalogue: agents,
    agents: filtered,
    tags,
    category,
    onCategoryChange,
    selectedTags,
    onSelectedTagsChange,
    searchText,
    onSearch,
  };
};
